import { useCallback, useEffect, useMemo, useState } from "react";
import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import type { Issue, Job } from "@/data/types";
import {
  IntakeReviewPageService,
  type IntakeReviewJobEntry,
  type IntakeReviewJobsPageQuery,
  type IntakeReviewSummary,
  type PageResponse,
} from "@/services/intake-review-page.service";

function emptyPage<T>(): PageResponse<T> {
  return { data: [], count: 0, error: null };
}

export function useIntakeReviewJobsPage(query: IntakeReviewJobsPageQuery & { enabled?: boolean }) {
  const [state, setState] = useState<PageResponse<IntakeReviewJobEntry>>(emptyPage());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setState(emptyPage());
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void IntakeReviewPageService.getJobsPage(query).then((result) => {
      if (cancelled) return;
      setState(result);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [
    query.enabled,
    query.page,
    query.pageSize,
    query.search,
    query.lane,
    query.bucket,
    query.refreshToken,
  ]);

  return { ...state, loading };
}

export function useIntakeReviewSummary(query: { enabled?: boolean; refreshToken?: number }) {
  const [data, setData] = useState<IntakeReviewSummary | null>(null);
  const [error, setError] = useState<unknown | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!query.enabled) {
      setData(null);
      setError(null);
      setLoading(false);
      return () => { cancelled = true; };
    }
    setLoading(true);
    void IntakeReviewPageService.getSummary(query.refreshToken).then((result) => {
      if (cancelled) return;
      setData(result.data);
      setError(result.error);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [query.enabled, query.refreshToken]);

  return { data, error, loading };
}

export function useIntakeReviewServerData(query: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
  bucket?: IntakeReviewJobsPageQuery["bucket"];
}) {
  const [refreshToken, setRefreshToken] = useState(0);
  const page = useIntakeReviewJobsPage({
    enabled: query.enabled,
    page: query.page ?? 0,
    pageSize: query.pageSize ?? 20,
    search: query.search,
    bucket: query.bucket,
    refreshToken,
  });
  const summary = useIntakeReviewSummary({ enabled: query.enabled, refreshToken });

  const serverJobs = useMemo(() => page.data.map((entry) => entry.job), [page.data]);
  const serverEvents = useMemo(
    () => page.data.flatMap((entry) => entry.buildReviewEvents),
    [page.data],
  );
  const serverIssues = useMemo(
    () => page.data.flatMap((entry) => entry.openIssues),
    [page.data],
  );

  const [jobs, setJobs] = useState<Job[]>([]);
  const [buildReviewEvents, setBuildReviewEvents] = useState<BuildReviewEvent[]>([]);
  const [issues, setIssues] = useState<Issue[]>([]);

  useEffect(() => {
    setJobs(serverJobs);
  }, [serverJobs]);

  useEffect(() => {
    setBuildReviewEvents(serverEvents);
  }, [serverEvents]);

  useEffect(() => {
    setIssues(serverIssues);
  }, [serverIssues]);

  const refresh = useCallback(async () => {
    setRefreshToken((value) => value + 1);
  }, []);

  return {
    entries: page.data,
    count: page.count,
    error: page.error ?? summary.error,
    loading: page.loading || summary.loading,
    jobs,
    setJobs,
    buildReviewEvents,
    setBuildReviewEvents,
    issues,
    setIssues,
    summary: summary.data,
    refresh,
  };
}
